"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function ReplacementBlowers() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-header", {
        y: 12
      });
      gsap.to(".reveal-header", {
        opacity: 1,
        y: 0,
        duration: 0.35,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".reveal-body", {
        y: 12
      });
      gsap.to(".reveal-body", {
        opacity: 1,
        y: 0,
        duration: 0.45,
        stagger: 0.05,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reveal-header", ".reveal-body"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") {
      refresh();
    } else {
      window.addEventListener("load", refresh, {
        once: true
      });
    }
    if (document.fonts?.ready) {
      document.fonts.ready.then(refresh);
    }
  }, {
    scope: containerRef
  });
  return <section id="replacementblowersinfo" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div ref={containerRef} className="mx-auto max-w-3xl relative border border-border bg-background p-6 sm:p-10 md:p-14">
        {/* Machined datum corner tick marks */}
        <div className="absolute -top-1.5 -left-1.5 size-3 border-t-2 border-l-2 border-foreground/80 pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-1.5 -right-1.5 size-3 border-t-2 border-r-2 border-foreground/80 pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-1.5 -left-1.5 size-3 border-b-2 border-l-2 border-foreground/80 pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-1.5 -right-1.5 size-3 border-b-2 border-r-2 border-foreground/80 pointer-events-none" aria-hidden="true" />

        {/* Technical Eyebrow & Title */}
        <header className="reveal-header" style={{
        opacity: 0
      }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="size-1.5 bg-primary rounded-none inline-block" />
            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
              Equipment Specification Guidelines
            </span>
          </div>

          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground">
            Replacement Blowers for Mechanical Bull Inflatables
          </h2>
        </header>

        {/* Datum Line */}
        <Separator className="my-6 bg-border" />

        {/* Technical Advisory Content */}
        <div className="space-y-4 font-sans text-base sm:text-lg text-foreground/90 leading-relaxed">
          <p className="reveal-body" style={{
          opacity: 0
        }}>
            A replacement blower should be matched to the inflatable it needs to
            support. Air requirements can vary according to the inflatable
            design, size and construction, so buyers should not choose a blower
            only by appearance or connector size.
          </p>

          <p className="reveal-body" style={{
          opacity: 0
        }}>
            Review the specification of the existing inflatable and the
            information supplied with the replacement blower. If the correct
            requirement is unclear, ask before ordering.
          </p>

          <p className="reveal-body" style={{
          opacity: 0
        }}>
            Using the wrong blower can create poor inflation or make the setup
            harder to operate correctly.
          </p>
        </div>

        {/* Machined spec footer notation */}
        <div className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between font-mono text-2xs uppercase tracking-wider text-muted-foreground">
          <span>DOC REF: SEC-BLW-01</span>
          <span>AIR DISCHARGE MATCHING SPECIFICATION</span>
        </div>
      </div>
    </section>;
}